import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import ServiceDetailsForm, { ServiceData as DetailsServiceData } from './ServiceDetailsForm';
import ServiceImagesForm from './ServiceImagesForm';
import ServiceRequirementsForm from './ServiceRequirementsForm';
import { serviceManagementService } from '../../services/serviceManagementService';
import { topCleanersService } from '../../services/topCleanersService';

const steps = ['Service Details', 'Upload Images', 'Add Requirements'];

// Use the ServiceData type from ServiceDetailsForm

// export interface EquipmentItem {
//   id: number;
//   equipment_id: number;
//   quantity: string;
// }

// export interface RequirementItem {
//   id: number;
//   requirement_id: number; // keep as string in UI; convert to number on submit
//   is_mandatory?: boolean;
// }

export interface EquipmentItem {
  id: number;
  equipment_id: number ;
  quantity: number ;
}

export interface RequirementItem {
  id: number;
  requirement_id: number ;
  is_mandatory: boolean;
}

interface AddonServiceItem {
  id: number;
  addon_service_id: number;
  is_included: boolean;
}

interface ExistingImages {
  image_id: string | number;
  image_url: string;
}

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

const ServiceManagement: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const { serviceId } = useParams();
  // in ServiceManagement component top area
  const [topCleaners, setTopCleaners] = useState<any[]>([]);
  const [deletedTopCleanerIds, setDeletedTopCleanerIds] = useState<number[]>([]);
  
  const [serviceData, setServiceData] = useState<DetailsServiceData>({
    name: '',
    description: '',
    category_id: 0,
    duration: 30,
    price: 0,
    status: 1,
    display_order: 0,
  });
  
  const [equipment, setEquipment] = useState<EquipmentItem[]>([
    { id: 1, equipment_id: 0, quantity: 1 },
  ]);
  
  const [requirements, setRequirements] = useState<RequirementItem[]>([
    { id: 1, requirement_id: 0, is_mandatory: false },
  ]);
  
  const [addonServices, setAddonServices] = useState<AddonServiceItem[]>([
    { id: 1, addon_service_id: 0, is_included: false }
  ]);
  // const [addonServices, setAddonServices] = useState<AddonServiceItem[]>([]);
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState<SnackbarState>({
      open: false,
      message: '',
      severity: 'success'
    });
  
  const [existingImages, setExistingImages] = useState<ExistingImages[]>([]);

  // Load existing service data in edit mode
  useEffect(() => {
    const load = async () => {
      if (!serviceId) return;
      try {
        console.log('🔍 Loading service for edit, serviceId:', serviceId);
        setIsSubmitting(true);
        
        // Prefer complete data to also prefill relations
        const complete = await serviceManagementService.getCompleteService(Number(serviceId));
        console.log('✅ Loaded complete service data:', complete);
        
        if (!complete) {
          console.warn('⚠️ No service data returned');
          return;
        }

        // Map core fields to DetailsServiceData - handle different possible field names
        const mappedData = {
          service_id: complete.service_id || complete.id,
          category_id: complete.category_id,
          name: complete.name || complete.service_name || '',
          description: complete.description || '',
          duration: complete.duration || complete.duration_minutes || 30,
          price: parseFloat(complete.price) || parseFloat(complete.base_price) || parseFloat(complete.total_amount) || 0,
          discount_price: complete.discount_price ? parseFloat(complete.discount_price) : null,
          image_url: complete.image_url ?? null,
          status: complete.status !== undefined ? complete.status : (complete.is_active ? 1 : 0),
          display_order: complete.display_order || 0,
        };
        console.log('🔧 Mapped service data:', mappedData);
        setServiceData(mappedData);

        // Prefill equipment list
        if (Array.isArray(complete.equipment)) {
          const eqItems = complete.equipment.map((e: any, idx: number) => ({
            id: idx + 1,
            equipment_id: String(e.equipment_id || e.id),
            quantity: String(e.quantity || 1),
          }));
          console.log('🔧 Equipment items:', eqItems);
          setEquipment(eqItems.length ? eqItems : [{ id: 1, equipment_id: '', quantity: 1 }]);
        }

        // Prefill requirements list
        if (Array.isArray(complete.requirements)) {
          const reqItems = complete.requirements.map((r: any, idx: number) => ({
            id: idx + 1,
            requirement_id: String(r.requirement_id || r.id),
            is_mandatory: !!r.is_mandatory,
          }));
          console.log('🔧 Requirement items:', reqItems);
          setRequirements(reqItems.length ? reqItems : [{ id: 1, requirement_id: '' }]);
        }

        // Prefill addons list
        if (Array.isArray(complete.addons)) {
        //   const addonItems = complete.addons.map((r: any, idx: number) => ({
        //     id: idx + 1,
        //     addon_id: String(r.addon_id || r.id)
        //   }));
        //   console.log('🔧 Addons items:', addonItems);
        //   setAddonServices(addonItems.length ? addonItems : [{ id: 1, addon_id: '' }]);
          const addonItems = complete.addons.map((r: any, idx: number) => ({
            id: idx + 1,
            addon_service_id: Number(r.addon_id || r.id),
            is_included: true
          }));
            console.log('🔧 Addons items:', addonItems);
          setAddonServices(addonItems.length ? addonItems : [{ id: 1, addon_id: '' }]);
        }

         const serviceImages = Array.isArray(complete.images) ? complete.images : [];
        // existing images list
        if (Array.isArray(complete.images)) {
          const existImages = complete.images.map((img: any, idx: number) => ({
            image_id: String(img.image_id || img.id),
            image_url: String(img.image_url || ''),
          }));
          console.log('🔧 Existing Images:', existImages);
          setExistingImages(existImages.length ? existImages : [{ id: 1, image_id: '' }]);
        }

      } catch (e) {
        console.error('❌ Failed to load service', e);
        setError('Failed to load service details');
      } finally {
        setIsSubmitting(false);
      }
    };
    load();
  }, [serviceId]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

   const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleServiceSubmit = async () => {
    console.log('🔍 Starting service submission...');
    console.log('🔍 Service ID:', serviceId);
    console.log('🔍 Service Data:', serviceData);
    console.log('🔍 Top Cleaners:', topCleaners);
    console.log('🔍 Deleted Top Cleaner IDs:', deletedTopCleanerIds);
    
    setIsSubmitting(true);
    setError(null);

    try {
      let savedServiceId: number;
      if (serviceId) {
        // Update existing service
        console.log('🔧 Updating existing service...');
        await serviceManagementService.updateService(Number(serviceId), {
          ...serviceData,
        });
        savedServiceId = Number(serviceId);
        console.log('✅ Service updated successfully');
        showSnackbar("Service updated successfully", "success");
      } else {
        // Create new service
        console.log('🔧 Creating new service...');
        const serviceResponse = await serviceManagementService.createService({
          ...serviceData,
        });
        savedServiceId = serviceResponse.service_id;
        console.log('✅ Service created successfully, ID:', savedServiceId);
        showSnackbar("Service created successfully", "success");
      }

      // 2. Upload images if any
      if (images.length > 0) {
        console.log('🔧 Uploading images...', images.length);
        // Use multi-upload endpoint for both create and edit
        await serviceManagementService.uploadServiceImages(savedServiceId, images);
        console.log('✅ Images uploaded successfully');
      }

    /** FORMAT COMMON PAYLOADS **/
    const equipmentPayload = equipment
    .filter(e => e.equipment_id && Number(e.equipment_id) > 0)
    .map(e => ({
      equipment_id: Number(e.equipment_id),
      quantity: Number(e.quantity) || 1
    }));

  const requirementPayload = requirements
    .filter(r => r.requirement_id && Number(r.requirement_id) > 0)
    .map(r => ({
      requirement_id: Number(r.requirement_id),
      is_mandatory: Boolean(r.is_mandatory)
    }));

      // 3. Add equipment and requirements
      if (serviceId) {
        // In edit mode, replace in bulk
        const equipmentUpdates = equipment
          .filter((e) => e.equipment_id)
          .map((e) => ({
            equipment_id: e.equipment_id,
            quantity: e.quantity ? e.quantity : 1
          }));
        
        // if (equipmentUpdates.length) {
        //   console.log('🔧 Updating equipment...', equipmentUpdates);
        //   await serviceManagementService.updateServiceEquipment(savedServiceId, equipmentUpdates);
        //   console.log('✅ Equipment updated successfully');
        // }
        
        const requirementUpdates = requirements
          .filter((r) => r.requirement_id)
          .map((r) => ({
            requirement_id: r.requirement_id,
            is_mandatory: r.is_mandatory || false
          }));
          
        // if (requirementUpdates.length) {
        //   console.log('🔧 Updating requirements...', requirementUpdates);
        //   await serviceManagementService.updateServiceRequirements(savedServiceId, requirementUpdates);
        //   console.log('✅ Requirements updated successfully');
        // }

      if (equipmentPayload.length) {
        await serviceManagementService.updateServiceEquipment(savedServiceId, equipmentPayload);
      }

      if (requirementPayload.length) {
        await serviceManagementService.updateServiceRequirements(savedServiceId, requirementPayload);
      }
        
        // Update addon services
        if (addonServices.length > 0) {
          const addonUpdates = addonServices
            .filter(a => a.addon_service_id)
            .map(a => ({
              addon_service_id: a.addon_service_id,
              is_included: true
            }));
            
          if (addonUpdates.length > 0) {
            console.log('🔧 Updating addon services...', addonUpdates);
            await serviceManagementService.updateServiceAddonServices(savedServiceId, addonUpdates);
            console.log('✅ Addon services updated successfully');
          }
        }

        // Process top cleaners - save new ones and handle deletions
        console.log('🔧 Processing top cleaners...', topCleaners.length);
        
        // Delete any cleaners marked for deletion
        if (deletedTopCleanerIds.length > 0) {
          console.log('🗑️ Deleting top cleaners...', deletedTopCleanerIds);
          await Promise.all(
            deletedTopCleanerIds.map(id => topCleanersService.deleteTopCleaner(id))
          );
          console.log('✅ Top cleaners deleted successfully');
        }

        // Save new top cleaners (those without top_cleaner_id)
        const newCleaners = topCleaners.filter(c => !c.top_cleaner_id);
        if (newCleaners.length > 0) {
          console.log('➕ Adding new top cleaners...', newCleaners.length);
          await Promise.all(
            newCleaners.map(cleaner => {
              const formData = new FormData();
              formData.append('sp_id', String(cleaner.sp_id));
              formData.append('cleaner_text', cleaner.cleaner_text);
              formData.append('display_order', String(cleaner.display_order));
              
              // Add icon file if available
              if (cleaner._tempIconFile) {
                formData.append('icon', cleaner._tempIconFile);
              } else {
                formData.append('icon', '');
              }
              
              return topCleanersService.addTopCleaner(savedServiceId, formData);
            })
          );
          console.log('✅ New top cleaners saved successfully');
        }

        // Update existing top cleaners if needed
        const existingCleaners = topCleaners.filter(c => c.top_cleaner_id);
        if (existingCleaners.length > 0) {
          console.log('🔄 Updating existing top cleaners...', existingCleaners.length);
          await Promise.all(
            existingCleaners.map(cleaner => {
              const formData = new FormData();
              formData.append('cleaner_text', cleaner.cleaner_text);
              formData.append('display_order', String(cleaner.display_order));
              
              return topCleanersService.updateTopCleaner(cleaner.top_cleaner_id!, formData);
            })
          );
          console.log('✅ Existing top cleaners updated successfully');
        }
      } else {
        // In create mode, add one-by-one
        console.log('🔧 Adding equipment and requirements for new service...');
        await Promise.all([
          ...equipment
            .filter((eq) => eq.equipment_id)
            .map((eq) =>
              serviceManagementService.updateServiceEquipment(savedServiceId, [{
                equipment_id: eq.equipment_id,
                quantity: eq.quantity ? eq.quantity : 1
              }])
            ),
          ...requirements
            .filter((req) => req.requirement_id)
            .map((req) =>
              serviceManagementService.updateServiceRequirements(savedServiceId, [{
                requirement_id: req.requirement_id,
                is_mandatory: req.is_mandatory ?? false,
              }])
            ),
        //   ...equipmentPayload.map(eq =>
        //   serviceManagementService.addServiceEquipment(savedServiceId, eq)
        // ),

        // ...requirementPayload.map(r =>
        //   serviceManagementService.addServiceRequirement(savedServiceId, r)
        // ),
          // Add addon services for new service
          ...addonServices
            .filter(a => a.addon_service_id)
            .map(a =>
              serviceManagementService.updateServiceAddonServices(savedServiceId, [{
                addon_service_id: a.addon_service_id,
                is_included: true
              }])
            ),
          // Add top cleaners for new service
          ...topCleaners
            .filter(c => !c.top_cleaner_id) // Only new cleaners in create mode
            .map(cleaner => {
              const formData = new FormData();
              formData.append('sp_id', String(cleaner.sp_id));
              formData.append('cleaner_text', cleaner.cleaner_text);
              formData.append('display_order', String(cleaner.display_order));
              
              // Add icon file if available
              if (cleaner._tempIconFile) {
                formData.append('icon', cleaner._tempIconFile);
              } else {
                formData.append('icon', '');
              }
              
              return topCleanersService.addTopCleaner(savedServiceId, formData);
            }),
        ]);
        console.log('✅ All related data added successfully');
      }

      console.log('🎉 Service submission completed successfully!');
      navigate('/services');
    } catch (err) {
      console.error('❌ Error during service submission:', err);
      setError('Failed to save service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ServiceDetailsForm
            onSuccess={async (data) => {
              setServiceData(data);
              setActiveStep(1);
            }}
            initialData={serviceData}
            isSubmitting={isSubmitting}
          />
        );
      case 1:
        return (
          <ServiceImagesForm
            images={images}
           existingImages={existingImages}
            onImagesChange={setImages}
          />
        );
      case 2:
        return (
            <ServiceRequirementsForm
              equipment={equipment}
              requirements={requirements}
              addonServices={addonServices}
              onEquipmentChange={setEquipment}
              onRequirementsChange={setRequirements}
              onAddonServicesChange={setAddonServices}
              onTopCleanersChange={setTopCleaners}
              onDeletedTopCleanerIdsChange={setDeletedTopCleanerIds}
              serviceId={serviceId ? parseInt(serviceId) : undefined}
              topCleaners={topCleaners}
            />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {serviceId ? 'Edit Service' : 'Add New Service'}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || isSubmitting}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleServiceSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Saving...' : 'Save Service'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
      />
    </Container>
  );
};

export default ServiceManagement;
